import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import pb from '../../../lib/pocketbase';

function fileUrl(record, fileField, thumb) {
  try {
    if (!record || !record[fileField]) return '';
    return pb.files.getURL(record, record[fileField], thumb ? { thumb } : undefined);
  } catch {
    return '';
  }
}

const emptyForm = {
  title: '',
  slug: '',
  excerpt: '',
  content_md: '',
  author: '',
  published_at: '',
  tags_json: '[]',
  category_id: '',
  is_published: true,
  sort_order: 0,
  cover_image: null,
};

const CONTENT_MD_MAX_LEN = 50000;

function safeJsonParse(str, fallback) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

function makeSlug(input) {
  return String(input || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function normalizeRelationId(value) {
  if (!value) return '';
  if (Array.isArray(value)) return String(value[0] || '');
  if (typeof value === 'object') {
    if (value?.id) return String(value.id);
    return '';
  }
  return String(value);
}

function AdminSiteBlogPostsPage() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const dragIndexRef = React.useRef(-1);

  const [posts, setPosts] = React.useState([]);
  const [categories, setCategories] = React.useState([]);

  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = React.useState('');
  const [removeExistingCover, setRemoveExistingCover] = React.useState(false);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      try {
        if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
      } catch {}
    };
  }, [coverPreviewUrl]);

  const fetchAll = async () => {
    setError('');
    try {
      const [pRes, cRes] = await Promise.all([
        pb.collection('site_blog_posts').getList(1, 200, { sort: 'sort_order, -published_at, -created', expand: 'category' }),
        pb.collection('site_blog_categories').getList(1, 200, { sort: 'sort_order, -created', filter: 'is_active = true' }),
      ]);
      setPosts(pRes?.items ?? []);
      setCategories(cRes?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur chargement des articles');
      setPosts([]);
    }
  };

  React.useEffect(() => {
    fetchAll();
  }, []);

  const persistOrder = async (orderedItems) => {
    try {
      await Promise.all(
        orderedItems.map((rec, idx) => pb.collection('site_blog_posts').update(rec.id, { sort_order: idx + 1 }))
      );
    } catch (e) {
      alert(e?.message || 'Erreur de ré-ordre');
    }
  };

  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === -1 || from === overIndex) return;
    setPosts((arr) => {
      const next = arr.slice();
      const [moved] = next.splice(from, 1);
      next.splice(overIndex, 0, moved);
      dragIndexRef.current = overIndex;
      return next;
    });
  };

  const onDrop = async () => {
    dragIndexRef.current = -1;
    const ordered = [...posts];
    await persistOrder(ordered);
    await fetchAll();
  };

  const openCreate = () => {
    setEditing(null);
    setSlugTouched(false);
    setDragOver(false);
    setCoverPreviewUrl('');
    setRemoveExistingCover(false);
    setForm({
      ...emptyForm,
      sort_order: posts.length ? Math.max(...posts.map(p => Number(p.sort_order || 0))) + 1 : 0,
      published_at: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setSlugTouched(true);
    setDragOver(false);
    setCoverPreviewUrl('');
    setRemoveExistingCover(false);
    setForm({
      title: p?.title || '',
      slug: p?.slug || '',
      excerpt: p?.excerpt || '',
      content_md: p?.content_md || '',
      author: p?.author || '',
      published_at: (p?.published_at ? String(p.published_at).slice(0, 10) : ''),
      tags_json: JSON.stringify(p?.tags ?? [], null, 2),
      category_id: normalizeRelationId(p?.category),
      is_published: p?.is_published !== false,
      sort_order: Number(p?.sort_order || 0),
      cover_image: null,
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setSlugTouched(false);
    setDragOver(false);
    setCoverPreviewUrl('');
    setRemoveExistingCover(false);
  };

  const handleTitleChange = (value) => {
    setForm((f) => {
      const next = { ...f, title: value };
      if (!slugTouched) {
        next.slug = makeSlug(value);
      }
      return next;
    });
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: value }));
  };

  const setCoverFile = (file) => {
    if (!(file instanceof File)) return;
    setRemoveExistingCover(false);
    setForm((f) => ({ ...f, cover_image: file }));
    try {
      if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
    } catch {}
    try {
      const url = URL.createObjectURL(file);
      setCoverPreviewUrl(url);
    } catch {
      setCoverPreviewUrl('');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (String(form.content_md || '').length > CONTENT_MD_MAX_LEN) {
        alert(`Le contenu (Markdown) dépasse la limite PocketBase de ${CONTENT_MD_MAX_LEN} caractères.`);
        return;
      }

      const data = new FormData();
      data.append('title', form.title || '');
      data.append('slug', form.slug || '');
      data.append('excerpt', form.excerpt || '');
      data.append('content_md', form.content_md || '');
      data.append('author', form.author || '');
      data.append('published_at', form.published_at || '');
      data.append('tags', JSON.stringify(safeJsonParse(form.tags_json, [])));
      data.append('category', normalizeRelationId(form.category_id));
      data.append('is_published', form.is_published ? 'true' : 'false');
      data.append('sort_order', String(Number(form.sort_order || 0)));
      if (removeExistingCover) data.append('cover_image', '');
      if (form.cover_image instanceof File) data.append('cover_image', form.cover_image);

      if (editing?.id) {
        await pb.collection('site_blog_posts').update(editing.id, data);
      } else {
        await pb.collection('site_blog_posts').create(data);
      }
      await fetchAll();
      closeModal();
    } catch (e) {
      const details = (() => {
        try {
          if (e?.data) return JSON.stringify(e.data, null, 2);
          if (e?.response?.data) return JSON.stringify(e.response.data, null, 2);
          return '';
        } catch {
          return '';
        }
      })();
      alert(`${e?.message || "Erreur d'enregistrement"}${details ? `\n\nDétails:\n${details}` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await pb.collection('site_blog_posts').delete(id);
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleTogglePublished = async (p) => {
    try {
      await pb.collection('site_blog_posts').update(p.id, { is_published: !(p?.is_published !== false) });
      await fetchAll();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la mise à jour');
    }
  };

  return (
    <AdminLayout title="">
      <h1 style={{ marginTop: 0 }}>Blog · Articles</h1>
      <p>Gérez les articles du blog (public).</p>

      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .toggle-pill{ cursor:pointer; border:1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text); padding:6px 10px; border-radius:999px; font-size:.85rem; }
        .toggle-pill.on{ border-color: rgba(34,197,94,0.55); box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
        .toggle-pill.off{ opacity:.8; }
        .grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(420px, 1fr)); gap: 12px; }
        @media (max-width: 520px){ .grid{ grid-template-columns: 1fr; } }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 980px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-body{ padding: 16px 20px; display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .modal-body .full{ grid-column: 1 / -1; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display:flex; gap: 12px; justify-content:flex-end; padding: 16px 20px; border-top:1px solid var(--admin-border); }

        .field{ display:flex; flex-direction:column; gap: 6px; }
        .field label{ font-size: .9rem; color: var(--admin-muted); }
        .field input[type="text"], .field input[type="date"], .field input[type="number"], .field textarea, .field select{
          background: var(--admin-panel-2);
          border:1px solid var(--admin-border);
          color: var(--admin-text);
          border-radius: 10px;
          padding: 10px 10px;
          outline:none;
        }
        .field textarea{ min-height: 140px; resize: vertical; }
        .field input:focus, .field textarea:focus, .field select:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        .field small{ color: var(--admin-muted); }
        .btn{ cursor:pointer; border:1px solid var(--admin-border); background: transparent; color: var(--admin-text); padding: 10px 12px; border-radius: 10px; }
        .btn:hover{ background: rgba(255,255,255,0.06); }
        .admin-shell.admin-light .btn:hover{ background: rgba(2,6,23,0.06); }
        .btn-solid{ border-color: rgba(79,70,229,0.6); background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); color:#fff; }
        .btn-solid:disabled{ opacity: .6; cursor: not-allowed; }
        .btn-danger{ border-color: rgba(239,68,68,0.5); }

        .thumb{ width: 64px; height: 44px; object-fit: cover; border-radius: 8px; border: 1px solid var(--admin-border); }

        .dropzone{
          border: 2px dashed var(--admin-border);
          border-radius: 12px;
          padding: 14px;
          background: var(--admin-panel-2);
          display:flex;
          gap: 12px;
          align-items: center;
          justify-content: space-between;
        }
        .dropzone.dragover{
          border-color: rgba(79,70,229,0.55);
          box-shadow: 0 0 0 3px rgba(79,70,229,0.16);
        }
        .dropzone-left{ display:flex; flex-direction:column; gap:4px; }
        .dropzone-title{ font-weight:700; }
        .dropzone-sub{ color: var(--admin-muted); font-size:.9rem; }
        .cover-preview{ width: 120px; height: 80px; border-radius: 10px; object-fit: cover; border:1px solid var(--admin-border); background: rgba(0,0,0,0.06); }
        .cover-thumb{ position: relative; width: 120px; height: 80px; }
        .cover-thumb .cover-preview{ width: 120px; height: 80px; }
        .thumb-actions{ position:absolute; right: 6px; top: 6px; display:flex; gap: 6px; }
        .thumb-actions button{ cursor:pointer; border:1px solid var(--admin-border); background: rgba(0,0,0,0.45); color:#fff; padding: 6px; border-radius: 10px; }
        .thumb-actions button:hover{ background: rgba(0,0,0,0.60); }
      `}</style>

      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Les articles non publiés ne s'affichent pas sur le site.</div>
        <button className="btn btn-solid btn-primary" onClick={openCreate}>Nouvel article</button>
      </div>

      <div className="grid">
        {posts.map((p, idx) => {
          const thumb = fileUrl(p, 'cover_image', '200x0');
          const catName = p?.expand?.category?.name || '';
          return (
            <div
              key={p.id}
              className="card"
              draggable
              onDragStart={(e) => onDragStart(e, idx)}
              onDragOver={(e) => onDragOver(e, idx)}
              onDrop={(e) => {
                e.preventDefault();
                onDrop();
              }}
              title={`Glissez pour réordonner (position ${idx + 1})`}
              style={{ cursor: 'grab' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  {thumb ? <img className="thumb" src={thumb} alt="" /> : null}
                  <div>
                    <div style={{ fontWeight: 800, lineHeight: 1.2 }}>{p.title || '(Sans titre)'}</div>
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem', marginTop: 4 }}>
                      slug: {p.slug || '-'}{catName ? ` · catégorie: ${catName}` : ''}
                    </div>
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem', marginTop: 6 }}>ordre: {idx + 1}</div>
                  </div>
                </div>
                <button className={`toggle-pill ${(p?.is_published !== false) ? 'on' : 'off'}`} onClick={() => handleTogglePublished(p)}>
                  {(p?.is_published !== false) ? 'Publié' : 'Brouillon'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn" onClick={() => openEdit(p)}>Modifier</button>
                <button className="btn btn-danger" onClick={() => handleDelete(p.id)}>Supprimer</button>
              </div>
            </div>
          );
        })}
      </div>

      {open && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => {
          if (e.target?.classList?.contains('modal-overlay')) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editing?.id ? 'Modifier l’article' : 'Nouvel article'}</h3>
              <button className="close-btn" onClick={closeModal} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="field full">
                <label>Titre</label>
                <input type="text" value={form.title} onChange={(e) => handleTitleChange(e.target.value)} />
              </div>
              <div className="field">
                <label>Slug</label>
                <input type="text" value={form.slug} onChange={(e) => handleSlugChange(e.target.value)} placeholder="ex: mon-article" />
              </div>
              <div className="field">
                <label>Date</label>
                <input type="date" value={form.published_at} onChange={(e) => setForm(f => ({ ...f, published_at: e.target.value }))} />
              </div>
              <div className="field">
                <label>Auteur</label>
                <input type="text" value={form.author} onChange={(e) => setForm(f => ({ ...f, author: e.target.value }))} />
              </div>
              <div className="field">
                <label>Catégorie</label>
                <select value={normalizeRelationId(form.category_id)} onChange={(e) => setForm(f => ({ ...f, category_id: e.target.value }))}>
                  <option value="">(Aucune)</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Ordre (sort_order)</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Extrait</label>
                <textarea value={form.excerpt} onChange={(e) => setForm(f => ({ ...f, excerpt: e.target.value }))} />
              </div>
              <div className="field full">
                <label>Contenu (Markdown)</label>
                <textarea
                  value={form.content_md}
                  maxLength={CONTENT_MD_MAX_LEN}
                  onChange={(e) => setForm(f => ({ ...f, content_md: e.target.value }))}
                />
                <small>{String(form.content_md || '').length} / {CONTENT_MD_MAX_LEN}</small>
              </div>
              <div className="field full">
                <label>Tags (JSON)</label>
                <textarea value={form.tags_json} onChange={(e) => setForm(f => ({ ...f, tags_json: e.target.value }))} placeholder='["SEO", "UX"]' />
              </div>
              <div className="field">
                <label>Image couverture</label>
                <div
                  className={`dropzone ${dragOver ? 'dragover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const file = e.dataTransfer?.files?.[0];
                    if (file) setCoverFile(file);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="dropzone-left">
                    <div className="dropzone-title">Glisser-déposer une image</div>
                    <div className="dropzone-sub">ou cliquer pour choisir un fichier</div>
                    {removeExistingCover ? (
                      <div className="dropzone-sub" style={{ color: '#ef4444' }}>Image supprimée (en attente d’enregistrement)</div>
                    ) : null}
                  </div>
                  {(coverPreviewUrl || (editing?.id && fileUrl(editing, 'cover_image', '400x0'))) && !removeExistingCover ? (
                    <div className="cover-thumb" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <img
                        className="cover-preview"
                        src={coverPreviewUrl || fileUrl(editing, 'cover_image', '400x0')}
                        alt={coverPreviewUrl ? 'Aperçu' : 'Couverture actuelle'}
                      />
                      <div className="thumb-actions">
                        <button
                          type="button"
                          title="Supprimer l’image"
                          aria-label="Supprimer l’image"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            setForm((f) => ({ ...f, cover_image: null }));
                            try {
                              if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
                            } catch {}
                            setCoverPreviewUrl('');
                            if (fileInputRef.current) fileInputRef.current.value = '';
                            if (editing?.id) setRemoveExistingCover(true);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setCoverFile(file);
                  }}
                />
                <small>{editing?.id ? 'Si tu ne changes pas, on garde l’image actuelle.' : 'PNG/JPG/WebP conseillés.'}</small>
              </div>
              <div className="field">
                <label>Publié</label>
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm(f => ({ ...f, is_published: e.target.checked }))} />
              </div>
            </div>
            <div className="modal-actions">
              {editing?.id && (
                <button className="btn btn-danger" onClick={() => { closeModal(); handleDelete(editing.id); }}>Supprimer</button>
              )}
              <button className="btn" onClick={closeModal}>Annuler</button>
              <button className="btn btn-solid" onClick={handleSave} disabled={loading}>
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSiteBlogPostsPage;
